import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { environment } from '../../environment/environment';

export interface InstructorPlatformConfig {
  id?: number;
  instructorId?: number;
  name?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  domain?: string;
  instructorProfileImage?: string;
  introductoryVideoUrl?: string | null;
  code?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PlatformThemeService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router, { optional: true });
  private readonly baseUrl = environment.eduBaseURL;
  private readonly hostUrl = environment.eduBaseURL.replace(/\/api\/?$/, '');

  // Default Ashal branding
  readonly DEFAULT_PRIMARY = '#63518C';
  readonly DEFAULT_SECONDARY = '#F7D671';
  readonly DEFAULT_LOGO = '/assets/logo.svg';
  readonly DEFAULT_AVATAR = '/assets/student-home/teacher-avatar.jpg';
  readonly DEFAULT_NAME = 'أسهل';

  readonly TEMPORARY_INSTRUCTOR_ID = 43;

  // Reactive State Signals
  readonly currentConfig = signal<InstructorPlatformConfig | null>(null);
  readonly currentLogo = signal<string>(this.DEFAULT_LOGO);
  readonly currentName = signal<string>(this.DEFAULT_NAME);
  readonly currentInstructorProfileImage = signal<string>(this.DEFAULT_AVATAR);
  readonly currentPrimaryColor = signal<string>(this.DEFAULT_PRIMARY);
  readonly currentSecondaryColor = signal<string>(this.DEFAULT_SECONDARY);
  readonly isLoadingPlatform = signal<boolean>(false);

  constructor() {
    // Restore cached platform configuration immediately on startup
    this.restoreCachedPlatform();

    // Listen to router navigation to dynamically apply or exclude custom theming
    if (this.router) {
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          this.applyThemeForUrl(e.urlAfterRedirects || e.url);
        });
    }

    // Apply theme according to the current URL on startup
    this.applyThemeForUrl();
  }

  /**
   * Checks if the given URL is an excluded route (/website or /auth)
   * which must always preserve default Ashal branding.
   */
  isExcludedRoute(url?: string): boolean {
    if (typeof window === 'undefined') return false;
    const rawPath = url || this.router?.url || window.location.pathname || '';
    const clean = (rawPath.startsWith('/') ? rawPath : `/${rawPath}`)
      .split('?')[0]
      .split('#')[0]
      .toLowerCase();

    // 1. All website marketing/landing pages (/website, /website/home, etc.)
    if (clean.startsWith('/website')) return true;

    // 2. Root landing on main domain without subdomain (redirects to /website/home)
    if (clean === '/' || clean === '/home' || clean === '') {
      const hostname = window.location.hostname.toLowerCase();
      const isSubdomain =
        hostname.includes('.') &&
        !hostname.startsWith('www.') &&
        !hostname.startsWith('localhost') &&
        !hostname.startsWith('app.') &&
        !hostname.startsWith('api.');
      if (!isSubdomain) {
        return true;
      }
    }

    return false;
  }

  /**
   * Applies either default Ashal theme (for /website and /auth) or the custom platform theme.
   */
  applyThemeForUrl(url?: string): void {
    const isExcluded = this.isExcludedRoute(url);
    const config = this.currentConfig();

    if (isExcluded || !config) {
      // Apply Default Ashal Theme
      this.setCssVariables(this.DEFAULT_PRIMARY, this.DEFAULT_SECONDARY);
      this.currentPrimaryColor.set(this.DEFAULT_PRIMARY);
      this.currentSecondaryColor.set(this.DEFAULT_SECONDARY);
      this.currentLogo.set(this.DEFAULT_LOGO);
      this.currentName.set(this.DEFAULT_NAME);
      this.updateFavicon(this.DEFAULT_LOGO);
      this.updateDocumentTitle(this.DEFAULT_NAME);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme-route', 'default');
      }
    } else {
      // Apply Custom Instructor Platform Theme
      const primary = (config.primaryColor && config.primaryColor.trim()) || this.DEFAULT_PRIMARY;
      const secondary = (config.secondaryColor && config.secondaryColor.trim()) || this.DEFAULT_SECONDARY;
      this.setCssVariables(primary, secondary);
      this.currentPrimaryColor.set(primary);
      this.currentSecondaryColor.set(secondary);

      const resolvedLogo = this.resolveLogoUrl(config.logoUrl);
      this.currentLogo.set(resolvedLogo);
      this.updateFavicon(resolvedLogo);

      const platformName = config.name?.trim() || this.DEFAULT_NAME;
      this.currentName.set(platformName);
      this.updateDocumentTitle(platformName);

      const resolvedAvatar = this.resolveImageUrl(config.instructorProfileImage, this.DEFAULT_AVATAR);
      this.currentInstructorProfileImage.set(resolvedAvatar);

      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme-route', 'platform');
      }
    }
  }

  /**
   * Applies the theme and info from a platform config object
   */
  applyPlatformConfig(config: InstructorPlatformConfig): void {
    if (!config) return;

    this.currentConfig.set(config);

    // Store Cache & Instructor IDs
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('cached_platform_config', JSON.stringify(config));
        if (config.instructorId && Number(config.instructorId) > 0) {
          localStorage.setItem('cached_platform_instructor_id', String(config.instructorId));
          localStorage.setItem('current_instructor_id', String(config.instructorId));
        }
      } catch {}
    }

    // Apply appropriate theme based on route
    this.applyThemeForUrl();
  }

  /**
   * Alias for backward compatibility
   */
  applyPlatformTheme(config?: InstructorPlatformConfig): void {
    if (config) {
      this.applyPlatformConfig(config);
    } else {
      this.applyDefaultTheme();
    }
  }

  /**
   * Restores default Ashal platform theme and branding
   */
  applyDefaultTheme(): void {
    this.currentConfig.set(null);
    this.applyThemeForUrl();
  }

  /**
   * Fetches platform info from backend for a specific subdomain
   * GET /api/instructors/platform?subDomain={subDomain}
   */
  loadPlatformBySubdomain(subdomain: string): Observable<InstructorPlatformConfig | null> {
    if (!subdomain || !subdomain.trim()) {
      this.applyDefaultTheme();
      return of(null);
    }

    const cleanSub = subdomain.trim().toLowerCase();
    this.isLoadingPlatform.set(true);

    return this.http
      .get<any>(`${this.baseUrl}/instructors/platform`, {
        params: { subDomain: cleanSub },
        headers: { accept: 'text/plain' },
      })
      .pipe(
        map((res) => {
          this.isLoadingPlatform.set(false);
          // Handles both direct object and wrapped StandardResponse
          const config = (res && res.data) ? res.data : res;
          if (config && (config.name || config.logoUrl || config.primaryColor || config.domain || config.instructorId)) {
            this.applyPlatformConfig(config);
            return config as InstructorPlatformConfig;
          }
          return null;
        }),
        catchError((err) => {
          this.isLoadingPlatform.set(false);
          console.warn(`[PlatformThemeService] No platform configuration found for subdomain: "${cleanSub}"`, err);
          return of(null);
        })
      );
  }

  /**
   * Updates platform configuration on the backend
   * PUT /api/instructors/platform?instructorId={instructorId}
   */
  updatePlatformConfig(
    instructorId: number,
    payload: {
      name: string;
      logoUrl: string;
      primaryColor: string;
      secondaryColor: string;
      domain?: string | null;
    }
  ): Observable<any> {
    return this.http
      .put<any>(
        `${this.baseUrl}/instructors/platform`,
        payload,
        {
          params: { instructorId: String(instructorId) },
          headers: {
            accept: 'text/plain',
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        map((res) => {
          const config = res && res.data ? res.data : res;
          if (config) {
            this.applyPlatformConfig(config);
          }
          return config;
        })
      );
  }

  /**
   * Resolves image paths from the backend (handling /Files/..., /..., or full http/https URLs)
   */
  resolveImageUrl(path?: string | null, defaultFallback: string = ''): string {
    if (!path || typeof path !== 'string' || !path.trim()) {
      return defaultFallback;
    }
    const trimmed = path.trim();
    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('data:') ||
      trimmed.startsWith('blob:')
    ) {
      return trimmed;
    }
    if (trimmed.startsWith('/assets/') || trimmed.startsWith('assets/')) {
      return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    }
    if (trimmed.startsWith('/')) {
      return `${this.hostUrl}${trimmed}`;
    }
    return `${this.hostUrl}/${trimmed}`;
  }

  /**
   * Resolves logo URL with default fallback
   */
  resolveLogoUrl(rawLogoUrl?: string | null): string {
    return this.resolveImageUrl(rawLogoUrl, this.DEFAULT_LOGO);
  }

  /**
   * Error fallback handler for <img> tags
   */
  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && img.src !== this.DEFAULT_LOGO) {
      img.src = this.DEFAULT_LOGO;
    }
  }

  /**
   * Sets CSS custom properties on document :root for dynamic live styling
   */
  private setCssVariables(primary: string, secondary: string): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    // Master Theme Variables
    root.style.setProperty('--primary-color', primary);
    root.style.setProperty('--secondary-color', secondary);

    // Aliases
    root.style.setProperty('--main-color', primary);
    root.style.setProperty('--second-color', secondary);
    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-second', secondary);
    root.style.setProperty('--color-accent-yellow', secondary);
    root.style.setProperty('--color-accent-gold', secondary);
    root.style.setProperty('--yellow-500', secondary);
    root.style.setProperty('--yellow-600', secondary);
    root.style.setProperty('--violet-500', primary);

    // Component UI
    root.style.setProperty('--p-button-primary-background', primary);
    root.style.setProperty('--p-button-primary-hover-background', primary);
    root.style.setProperty('--p-toggleswitch-checked-background', primary);
    root.style.setProperty('--p-toggleswitch-checked-hover-background', primary);
    root.style.setProperty('--atp-color--primary', primary);

    // Dynamic Style Tag for absolute override assurance
    this.injectDynamicThemeStyle(primary, secondary);
  }

  /**
   * Injects or updates a <style> tag in document head with !important overrides
   */
  private injectDynamicThemeStyle(primary: string, secondary: string): void {
    if (typeof document === 'undefined') return;
    let styleEl = document.getElementById('dynamic-platform-theme') as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'dynamic-platform-theme';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      :root {
        --primary-color: ${primary} !important;
        --secondary-color: ${secondary} !important;
        --main-color: ${primary} !important;
        --second-color: ${secondary} !important;
        --color-primary: ${primary} !important;
        --color-second: ${secondary} !important;
        --color-accent-yellow: ${secondary} !important;
        --color-accent-gold: ${secondary} !important;
        --yellow-500: ${secondary} !important;
        --yellow-600: ${secondary} !important;
        --violet-500: ${primary} !important;
        --p-button-primary-background: ${primary} !important;
        --p-button-primary-hover-background: ${primary} !important;
        --p-toggleswitch-checked-background: ${primary} !important;
        --p-toggleswitch-checked-hover-background: ${primary} !important;
        --atp-color--primary: ${primary} !important;
      }
      .color-main, .color-primary, .text-primary-color, .text-main-color {
        color: ${primary} !important;
      }
      .color-second, .text-secondary-color, .text-second-color {
        color: ${secondary} !important;
      }
      .bg-primary-color, .bg-main-color {
        background-color: ${primary} !important;
      }
      .bg-secondary-color, .bg-second-color {
        background-color: ${secondary} !important;
      }
      .border-primary-color, .border-main-color {
        border-color: ${primary} !important;
      }
      .border-secondary-color, .border-second-color {
        border-color: ${secondary} !important;
      }
    `;
  }

  /**
   * Dynamically updates the browser favicon to the platform logo
   */
  private updateFavicon(iconUrl: string): void {
    if (typeof document === 'undefined' || !iconUrl) return;
    let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      document.getElementsByTagName('head')[0]?.appendChild(link);
    }
    link.href = iconUrl;
  }

  /**
   * Updates browser title
   */
  private updateDocumentTitle(name: string): void {
    if (typeof document === 'undefined' || !name) return;
    if (name && name !== this.DEFAULT_NAME) {
      document.title = `${name} | منصة أسهل التعليمية`;
    }
  }

  /**
   * Detects subdomain from window location or query parameters
   */
  private detectSubdomain(): string | null {
    if (typeof window === 'undefined' || !window.location) return null;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('subDomain') || urlParams.has('subdomain')) {
        const querySub = urlParams.get('subDomain') || urlParams.get('subdomain');
        if (
          querySub &&
          querySub.trim() &&
          querySub.trim().toLowerCase() !== 'none' &&
          querySub.trim().toLowerCase() !== 'main'
        ) {
          return querySub.trim().toLowerCase();
        }
        return null;
      }
    } catch {}

    const hostname = window.location.hostname.toLowerCase();
    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
      try {
        const sessionSub = sessionStorage.getItem('active_subdomain');
        if (sessionSub) return sessionSub;
      } catch {}
      return null;
    }
    if (hostname.endsWith('localhost')) {
      const parts = hostname.split('.');
      if (parts.length > 1 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        return parts[0];
      }
      try {
        const sessionSub = sessionStorage.getItem('active_subdomain');
        if (sessionSub) return sessionSub;
      } catch {}
      return null;
    }
    if (hostname.endsWith('.vercel.app')) {
      const parts = hostname.replace('.vercel.app', '').split('.');
      if (parts.length > 1 && parts[0] !== 'www') return parts[0];
      return null;
    }
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const sub = parts[0];
      if (sub !== 'www' && sub !== 'app' && sub !== 'api') return sub;
    }
    return null;
  }

  /**
   * Restores cached platform on construction and fetches latest platform config for active subdomain
   */
  private restoreCachedPlatform(): void {
    const sub = this.detectSubdomain();
    if (!sub) {
      this.applyDefaultTheme();
      return;
    }

    let restoredFromCache = false;
    if (typeof localStorage !== 'undefined') {
      try {
        const cached = localStorage.getItem('cached_platform_config');
        if (cached) {
          const config = JSON.parse(cached);
          if (config && (config.primaryColor || config.name || config.logoUrl)) {
            if (!config.subDomain || config.subDomain.toLowerCase() === sub.toLowerCase()) {
              this.applyPlatformConfig(config);
              restoredFromCache = true;
            }
          }
        }
      } catch {}
    }

    this.loadPlatformBySubdomain(sub).subscribe();
  }
}
